import React, { useState, useContext, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ReactMarkdown from 'react-markdown';
import { Button, Collapse } from 'reactstrap';
import { MessagingSendMessageMutation } from '../Mutations';
import { GET_CONVERSATION } from '../Queries';
import { CurrentUserContext } from '../../Context/CurrentUserContext';
import { plainTextToHtml, htmlToPlainText } from './htmlText';
import { buildReplyRecipients, parseAddressList, EMPTY_RECIPIENTS } from './emailRecipients';
import FileUpload from './components/FileUpload';
import ScheduleModal from './components/ScheduleModal';
import MessageBodyEditor from './components/MessageBodyEditor';
import EmailRecipientFields from './components/EmailRecipientFields';
import './MessageComposer.css';
import { PreferredDateTimeContext } from '../../Context/PreferredDateTimeContext';
import ConversationNoteComposer from './ConversationNoteComposer';

export default function MessageComposer({ conversationId, suggestedResponse, channelType: channelTypeProp, onSend: onSendProp, hideNoteComposer = false, lastInboundMessage = null }) {
    const { currentUser } = useContext(CurrentUserContext);
    const [isMinimized, setIsMinimized] = useState(false);
    const [message, setMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [attachmentResetKey, setAttachmentResetKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeSuggestion, setActiveSuggestion] = useState('');
    const [scheduledTime, setScheduledTime] = useState(null);
    const [isScheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [messageHtml, setMessageHtml] = useState('');
    const [recipients, setRecipients] = useState(EMPTY_RECIPIENTS);
    const [recipientsValid, setRecipientsValid] = useState(true);
    const {preferredDateTimeInfo} = useContext(PreferredDateTimeContext);
    const composerRef = useRef(null);
    
    const [messagingSendMessageMutation] = useMutation(MessagingSendMessageMutation);
    
    // Get conversation to fetch inbox members for mentions
    const { data: conversationData } = useQuery(GET_CONVERSATION, {
        variables: { id: conversationId },
        skip: !conversationId,
        fetchPolicy: 'cache-first'
    });
    const conversation = conversationData?.conversation;
    const driverId = conversation?.driver?.id;
    const isEmailChannel = channelTypeProp !== undefined
        ? channelTypeProp === 'EMAIL'
        : conversation?.channel?.channelType === 'EMAIL';
    
    useEffect(() => {
        setActiveSuggestion(suggestedResponse || '');
    }, [suggestedResponse]);

    // Reply mode only — in new-conversation mode the modal owns the recipient fields.
    const isReplyMode = !onSendProp;
    const showRecipientFields = isEmailChannel && isReplyMode;
    const recipientsPrefillKey = `${conversationId || ''}:${lastInboundMessage?.id || ''}`;

    useEffect(() => {
        if (!showRecipientFields) return;
        setRecipients(buildReplyRecipients({ lastInboundMessage, conversation }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRecipientFields, recipientsPrefillKey, conversation?.contactEmail, conversation?.channel?.sendAs]);

    const readFileAsBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const result = reader.result;

                if (typeof result !== 'string') {
                    reject(new Error(`Could not read "${file.name}" as base64.`));
                    return;
                }

                const commaIndex = result.indexOf(',');
                if (commaIndex === -1) {
                    reject(new Error(`Invalid file data for "${file.name}".`));
                    return;
                }

                resolve({
                    name: file.name,
                    type: file.type || 'application/octet-stream',
                    data: result.slice(commaIndex + 1),
                });
            };

            reader.onerror = () => {
                reject(
                    reader.error ??
                    new Error(`Failed to read attachment "${file.name}".`)
                );
            };

            reader.onabort = () => {
                reject(new Error(`Reading attachment "${file.name}" was cancelled.`));
            };

            reader.readAsDataURL(file);
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const plainBody = isEmailChannel ? message.trim() : message;
        const htmlBody = isEmailChannel ? (messageHtml || plainTextToHtml(message)).trim() : '';
        if (loading || (!plainBody && selectedFiles.length === 0)) return;
        if (showRecipientFields && !recipientsValid) return;
        
        // If scheduled, just store it client-side for now
        let scheduledForTimestamp = null;
        if (scheduledTime) {
            scheduledForTimestamp = scheduledTime.unix();
        }
        setLoading(true);
        setError(null);

        try {
            const filesToUpload = await Promise.all(
                selectedFiles.map(readFileAsBase64)
            );

            if (onSendProp) {
                await onSendProp(plainBody, isEmailChannel ? htmlBody : '', filesToUpload);
            } else {
                let variables = {
                    conversationId,
                    body: plainBody || (selectedFiles.length > 0 ? '📎 Attachment' : ''),
                    files: filesToUpload,
                };
                if (isEmailChannel && htmlBody && plainBody) {
                    variables.bodyHtml = htmlBody;
                }
                if (showRecipientFields) {
                    // Only send keys that have content, so non-email channels and untouched fields
                    // keep exactly the payload they had before recipients were configurable.
                    const to = parseAddressList(recipients.to);
                    const cc = parseAddressList(recipients.cc);
                    const bcc = parseAddressList(recipients.bcc);
                    const replyTo = parseAddressList(recipients.replyTo);
                    if (to.length) variables.to = to.join(', ');
                    if (cc.length) variables.cc = cc;
                    if (bcc.length) variables.bcc = bcc;
                    if (replyTo.length) variables.replyTo = replyTo;
                }
                if (scheduledForTimestamp) {
                    variables.scheduledForTimestamp = scheduledForTimestamp;
                }
                const response = await messagingSendMessageMutation({ variables });

                const result = response?.data?.messagingSendMessage;
                if (!result?.ok) {
                    console.error('MessageComposer error: ', result?.errors);
                    setError(result?.errors?.toString() || 'Unknown error');
                    setLoading(false);
                    return;
                }
            }

            setLoading(false);
            setMessage('');
            setMessageHtml('');
            setSelectedFiles([]);
            setAttachmentResetKey(prev => prev + 1);
            setScheduledTime(null);
        } catch (err) {
            console.error('MessageComposer error:', err);
            setLoading(false);
            setError('Error sending message' + err.toString());
        }
    };

    const handleAcceptSuggestion = () => {
        if (!activeSuggestion) return;
        if (isEmailChannel) {
            const suggestionHtml = activeSuggestion
                .split('\n')
                .map(line => `<p>${line || '<br />'}</p>`)
                .join('');
            setMessageHtml(suggestionHtml);
            setMessage(htmlToPlainText(suggestionHtml));
        } else {
            setMessage(activeSuggestion);
        }
        setActiveSuggestion('');
    };

    const handleRejectSuggestion = () => {
        setActiveSuggestion('');
    };

    const handleFilesSelected = (files) => {
        setSelectedFiles(files);
    };

    const handleBodyChange = (plainText, html = '') => {
        setMessage(plainText);
        setMessageHtml(html);
    };

    const handleCommandSelect = (command) => {
        if (command === 'schedule') {
            setScheduleModalOpen(true);
        } else if (command === 'attach') {
            // Trigger file upload click
            const fileInput = document.querySelector('.file-upload-container input[type="file"]');
            if (fileInput) {
                fileInput.click();
            }
        }
    };

    const formatScheduledTime = (time) => {
        if (!time) return '';
        return new Date(time).toLocaleString();
    };

    // Stale validity from an unmounted field block must not block sending on another channel.
    const recipientsBlockSend = showRecipientFields
        && (!recipientsValid || parseAddressList(recipients.to).length === 0);
    const sendDisabled = loading
        || (!message.trim() && selectedFiles.length === 0)
        || recipientsBlockSend;

    if (!currentUser) return <div></div>;

    return (
        <div className="CommsSessionInput" ref={composerRef}>
            {isMinimized ? (
                <div
                    onClick={() => setIsMinimized(false)}
                    className="comms-composer-collapsed-bar"
                >
                    <i className="fa fa-chevron-up" aria-hidden="true" />
                    <span>
                        {message.trim() ? (
                            <><strong>Draft:</strong> {message.trim().substring(0, 50)}{message.trim().length > 50 ? '…' : ''}</>
                        ) : <strong>Reply</strong>}
                    </span>
                </div>
            ) : (
                <div
                    onClick={() => setIsMinimized(true)}
                    style={{ position: 'absolute', top: '20px', right: '24px', cursor: 'pointer', zIndex: 2 }}
                >
                    <i className="fa fa-chevron-down" aria-hidden="true" />
                </div>
            )}
            {error && <div className="alert alert-danger mt-2">{error}</div>}
            <Collapse isOpen={!isMinimized}>
            <form onSubmit={handleSubmit} className="comms-send-form">
                {activeSuggestion && (
                    <div className="CommsSuggestedResponse">
                        <div className="CommsSuggestedResponseHeader">
                            <span className="CommsSuggestedResponseLabel">Suggested response</span>
                        </div>
                        <div className="CommsSuggestedResponseBody">
                            <ReactMarkdown>{activeSuggestion}</ReactMarkdown>
                        </div>
                        <div className="CommsSuggestedResponseActions">
                            <button
                                type="button"
                                className="CommsSuggestedResponseButton reject"
                                onClick={handleRejectSuggestion}
                                disabled={loading}
                            >
                                Reject
                            </button>
                            <button
                                type="button"
                                className="CommsSuggestedResponseButton accept"
                                onClick={handleAcceptSuggestion}
                                disabled={loading}
                            >
                                Use suggestion
                            </button>
                        </div>
                    </div>
                )}
                
                {showRecipientFields && (
                    <EmailRecipientFields
                        value={recipients}
                        onChange={setRecipients}
                        onValidityChange={setRecipientsValid}
                        disabled={loading}
                        prefillKey={recipientsPrefillKey}
                    />
                )}

                {scheduledTime && (
                    <div style={{
                        padding: '8px 12px', 
                        background: '#fef3c7', 
                        border: '1px solid #fbbf24', 
                        borderRadius: '6px', 
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ fontSize: '13px', color: '#92400e' }}>
                            <i className="fa fa-clock-o" style={{ marginRight: '6px' }}></i>
                            Scheduled for {formatScheduledTime(scheduledTime)}
                        </span>
                        <Button
                            color="link"
                            size="sm"
                            onClick={() => setScheduledTime(null)}
                            style={{ padding: '4px', fontSize: '12px', backgroundColor: '#92400e' }}
                        >
                            Clear
                        </Button>
                    </div>
                )}
                
                <MessageBodyEditor
                    richText={isEmailChannel}
                    value={message}
                    valueHtml={messageHtml}
                    onChange={handleBodyChange}
                    disabled={loading}
                    placeholder={isEmailChannel ? 'Type an email...' : 'Type a message... Use / for commands'}
                    onSubmit={handleSubmit}
                    onCommandSelect={handleCommandSelect}
                    toolbarStart={
                        <div style={{ position: 'relative' }}>
                            <FileUpload
                                onFilesSelected={handleFilesSelected}
                                disabled={loading}
                                resetKey={attachmentResetKey}
                            />
                        </div>
                    }
                    toolbarEnd={
                        <>
                            <Button
                                type="button"
                                color="light"
                                size="sm"
                                onClick={() => setScheduleModalOpen(true)}
                                disabled={sendDisabled}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                                <i className="fa fa-clock-o" aria-hidden="true"></i>
                                <span>Schedule</span>
                            </Button>
                            <div className="comms-send-toolbar-spacer" />
                            <Button
                                type="submit"
                                color="primary"
                                size="sm"
                                disabled={sendDisabled}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                                {loading ? (
                                    scheduledTime ? 'Scheduling message...' : 'Sending...'
                                ) : scheduledTime ? (
                                    <>
                                        <i className="fa fa-clock-o" aria-hidden="true"></i>
                                        <span>Confirm schedule</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-paper-plane" aria-hidden="true"></i>
                                        <span>Send</span>
                                    </>
                                )}
                            </Button>
                        </>
                    }
                />
            </form>
            </Collapse>
            {isScheduleModalOpen &&
                <ScheduleModal
                    isOpen={isScheduleModalOpen}
                    toggle={() => setScheduleModalOpen(!isScheduleModalOpen)}
                    onSchedule={(time) => {
                        setScheduledTime(time);
                        setScheduleModalOpen(false);
                    }}
                    driverId={driverId}
                    userPrefferedTz={preferredDateTimeInfo?.timezone}
                />
            }
            {!hideNoteComposer && <ConversationNoteComposer conversationId={conversationId}/>}
        </div>
    );
}
